'use client';

import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import {
  parseMarkdownSections,
  stripSectionNumber,
  parseParagraphs,
  parseBulletList,
  type MarkdownSection,
} from './markdown-sections';
import { SectionCard } from '../../definition/templates/section-card';
import { TagList } from '../../definition/templates/tag-list';

interface ApplicationAreasTemplateProps {
  markdown: string;
  className?: string;
}

const AREA_COLORS = [
  { dot: 'bg-indigo-500', titleColor: 'text-indigo-900', border: 'border-indigo-200', accent: 'bg-indigo-500' },
  { dot: 'bg-emerald-500', titleColor: 'text-emerald-900', border: 'border-emerald-200', accent: 'bg-emerald-500' },
  { dot: 'bg-violet-500', titleColor: 'text-violet-900', border: 'border-violet-200', accent: 'bg-violet-500' },
  { dot: 'bg-amber-500', titleColor: 'text-amber-900', border: 'border-amber-200', accent: 'bg-amber-500' },
  { dot: 'bg-cyan-500', titleColor: 'text-cyan-900', border: 'border-cyan-200', accent: 'bg-cyan-500' },
  { dot: 'bg-rose-500', titleColor: 'text-rose-900', border: 'border-rose-200', accent: 'bg-rose-500' },
  { dot: 'bg-blue-500', titleColor: 'text-blue-900', border: 'border-blue-200', accent: 'bg-blue-500' },
  { dot: 'bg-orange-500', titleColor: 'text-orange-900', border: 'border-orange-200', accent: 'bg-orange-500' },
];

function RichParagraph({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return (
    <p className="text-[15px] text-gray-700 leading-relaxed mb-3">
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i} className="font-semibold text-indigo-700">{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith('`') && part.endsWith('`')) {
          return (
            <code key={i} className="bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded text-sm font-mono border border-indigo-100">
              {part.slice(1, -1)}
            </code>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </p>
  );
}

function BodyContent({ body }: { body: string }) {
  if (!body) return null;
  const paragraphs = parseParagraphs(body);
  if (paragraphs.length === 0) return null;
  return (
    <div>
      {paragraphs.map((p, i) => (
        <RichParagraph key={i} text={p} />
      ))}
    </div>
  );
}

function BulletList({ items }: { items: string[] }) {
  if (items.length === 0) return null;
  return (
    <ul className="space-y-1.5">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
          <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 shrink-0" />
          <span><RichParagraph text={item} /></span>
        </li>
      ))}
    </ul>
  );
}

function SubSection({ section }: { section: MarkdownSection }) {
  const bullets = parseBulletList(section.body);
  const paragraphs = parseParagraphs(section.body);

  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-800 mb-2">
        {section.title}
      </h4>
      {bullets.length > 0 ? (
        <BulletList items={bullets} />
      ) : paragraphs.length > 0 ? (
        <BodyContent body={section.body} />
      ) : null}
    </div>
  );
}

function AreaCard({ section, colorIndex }: { section: MarkdownSection; colorIndex: number }) {
  const color = AREA_COLORS[colorIndex % AREA_COLORS.length];

  const capabilitiesSection = section.children.find(c =>
    c.title.toLowerCase().includes('capabilities') || c.title.toLowerCase().includes('features')
  );
  const workflowsSection = section.children.find(c =>
    c.title.toLowerCase().includes('workflow')
  );
  const dataSection = section.children.find(c =>
    c.title.toLowerCase().includes('data') || c.title.toLowerCase().includes('integration')
  );
  const metricsSection = section.children.find(c =>
    c.title.toLowerCase().includes('metric') || c.title.toLowerCase().includes('kpi') || c.title.toLowerCase().includes('success')
  );
  const descSection = section.children.find(c =>
    c.title.toLowerCase().includes('description') || c.title.toLowerCase().includes('name')
  );

  const otherSections = section.children.filter(c =>
    c !== capabilitiesSection && c !== workflowsSection &&
    c !== dataSection && c !== metricsSection && c !== descSection
  );

  const metricsList = metricsSection ? parseBulletList(metricsSection.body) : [];

  return (
    <div className={cn('bg-white border rounded-xl overflow-hidden', color.border)}>
      <div className={cn('h-1.5', color.accent)} />
      <div className="p-6">
        <h3 className={cn('text-lg font-semibold mb-3', color.titleColor)}>
          {stripSectionNumber(section.title)}
        </h3>

        {section.body && <BodyContent body={section.body} />}
        {descSection && <BodyContent body={descSection.body} />}

        <div className="space-y-4 mt-4">
          {capabilitiesSection && <SubSection section={capabilitiesSection} />}
          {workflowsSection && <SubSection section={workflowsSection} />}
          {dataSection && <SubSection section={dataSection} />}

          {otherSections.map((s, i) => (
            <SubSection key={i} section={s} />
          ))}

          {metricsList.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-800 mb-2">Success Metrics</h4>
              <TagList tags={metricsList.map(m => m.replace(/\*\*([^*]+)\*\*/g, '$1'))} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function isOverviewSection(title: string): boolean {
  const t = stripSectionNumber(title).toLowerCase();
  return t === 'overview' || t === 'application areas overview' || t.includes('overview');
}

function isInteractionMapSection(title: string): boolean {
  const t = stripSectionNumber(title).toLowerCase();
  return t.includes('interaction map') || t.includes('area interaction') || t.includes('dependency');
}

export function ApplicationAreasTemplate({ markdown, className }: ApplicationAreasTemplateProps) {
  const parsed = useMemo(() => parseMarkdownSections(markdown), [markdown]);

  if (parsed.sections.length === 0) {
    return (
      <div className={cn('prose prose-base max-w-none text-gray-700', className)}>
        <p>{markdown}</p>
      </div>
    );
  }

  const overviewSection = parsed.sections.find(s => isOverviewSection(s.title));
  const interactionSection = parsed.sections.find(s => isInteractionMapSection(s.title));
  const areaSections = parsed.sections.filter(
    s => !isOverviewSection(s.title) && !isInteractionMapSection(s.title)
  );

  return (
    <div className={cn('space-y-6', className)}>
      {overviewSection && (
        <SectionCard
          title={stripSectionNumber(overviewSection.title)}
          dotColor="bg-indigo-500"
          titleColor="text-indigo-900"
        >
          <BodyContent body={overviewSection.body} />
          {overviewSection.children.length > 0 && (
            <div className="space-y-3 mt-3">
              {overviewSection.children.map((child, i) => (
                <SubSection key={i} section={child} />
              ))}
            </div>
          )}
        </SectionCard>
      )}

      {areaSections.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {areaSections.map((section, i) => (
            <AreaCard key={i} section={section} colorIndex={i} />
          ))}
        </div>
      )}

      {interactionSection && (
        <SectionCard
          title={stripSectionNumber(interactionSection.title)}
          dotColor="bg-violet-500"
          titleColor="text-violet-900"
        >
          <BodyContent body={interactionSection.body} />
          {interactionSection.children.length > 0 && (
            <div className="space-y-3 mt-3">
              {interactionSection.children.map((child, i) => (
                <SubSection key={i} section={child} />
              ))}
            </div>
          )}
        </SectionCard>
      )}
    </div>
  );
}
